import { Crown, GraduationCap, Heart, ArrowRight, LucideIcon, Star, Target, Lightbulb, Users, Briefcase } from "lucide-react";
import { useWebsiteContent } from "@/hooks/useWebsiteContent";
import { useScrollReveal } from "@/hooks/useScrollReveal";

const iconMap: Record<string, LucideIcon> = {
  Crown, GraduationCap, Heart, Star, Target, Lightbulb, Users, Briefcase,
};

const Services = () => {
  const { content } = useWebsiteContent();
  const { ref: sectionRef, isVisible } = useScrollReveal();
  const colorStyles: Record<string, string> = {
    primary: "border-primary/20 bg-primary/[0.02]",
    gold: "border-amber-400/25 bg-amber-50/30 dark:bg-amber-500/[0.05]",
    accent: "border-violet-400/25 bg-violet-50/40 dark:bg-violet-500/[0.06]",
  };

  return (
    <section id="services" className="py-20 md:py-28 bg-card">
      <div ref={sectionRef} className={`max-w-[980px] mx-auto px-4 md:px-6 scroll-reveal ${isVisible ? "is-visible" : ""}`}>
        {/* Header */}
        <div className="text-center max-w-[680px] mx-auto mb-16">
          <h2 className="text-4xl md:text-5xl lg:text-[56px] font-semibold text-foreground tracking-[-0.03em] leading-tight mb-4">
            {content.services_title}
          </h2>
          <p className="text-xl text-muted-foreground font-normal">
            {content.services_subtitle}
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {content.services.map((service, index) => {
            const Icon = iconMap[service.icon] || Crown;
            return (
              <article
                key={index}
                className={`group h-full border rounded-2xl p-8 transition-all duration-300 hover:shadow-md flex flex-col ${colorStyles[service.color] || "border-border bg-background"}`}
              >
                <div className="w-11 h-11 rounded-full bg-muted flex items-center justify-center mb-6">
                  <Icon className="w-5 h-5 text-foreground/60" />
                </div>

                <h3 className="text-lg font-semibold text-foreground mb-1 tracking-tight">
                  {service.title}
                </h3>
                <p className="text-sm text-foreground/50 font-medium mb-3">
                  {service.subtitle}
                </p>
                <p className="text-muted-foreground text-[15px] leading-relaxed mb-5">
                  {service.description}
                </p>

                <ul className="space-y-2 mb-6 flex-1">
                  {service.highlights.map((highlight, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-foreground/60">
                      <div className="w-1 h-1 rounded-full bg-muted-foreground/40" />
                      {highlight}
                    </li>
                  ))}
                </ul>

                <a href="#packages" className="inline-flex items-center gap-1 text-foreground text-sm font-normal hover:underline">
                  View Packages <ArrowRight className="w-3.5 h-3.5" />
                </a>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
